/**
 * Discover favorites (#2293): `api/profiles/self/favorites` answers the
 * caller's favorite models, documents and functionals, newest first. These
 * helpers tell the kinds apart, open each where the app shows it, filter the
 * shelf by kind, and pick what the featured carousel shows.
 *
 * The shelf shows models and functionals only. Nothing in the app lets you
 * favorite a document, so a document favorite (a leftover) is left out and
 * there is no Documents filter.
 */
import type { DiscoverItem } from './useDiscoverData';

export type FavoriteKind = 'model' | 'document' | 'functional';

/** The shelf's kind filter: every kind, or one. */
export type FavoriteFilter = 'all' | FavoriteKind;

export const FAVORITE_FILTERS: { value: FavoriteFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'model', label: 'Models' },
  { value: 'functional', label: 'Functionals' },
];

/** The favorites the shelf shows: every kind but documents, in the server's order. */
export function shelfFavorites(items: DiscoverItem[]): DiscoverItem[] {
  return items.filter((item) => favoriteKind(item) !== 'document');
}

/**
 * The kind of a favorite, from the reference or URI the server sends with it
 * (`urn:brew:documents:<id>`, `api/functionals/<id>`). Anything else is a
 * model, as every favorite was before #2293.
 */
export function favoriteKind(item: DiscoverItem): FavoriteKind {
  const where = `${item.$reference ?? ''} ${item.$uri ?? ''}`;
  if (/(urn:brew:|api\/)documents\b/.test(where)) return 'document';
  if (/(urn:brew:|api\/)functionals\b/.test(where)) return 'functional';
  return 'model';
}

/** Where a favorite opens: a model in Build, a document's resource page, a functional's help page. */
export function favoriteRoute(item: DiscoverItem): string {
  switch (favoriteKind(item)) {
    case 'document': return `/users/resource/${item._id}`;
    case 'functional': return `/learn/help/functionals/${item._id}`;
    default: return `/build/${item._id}`;
  }
}

export function filterFavorites(items: DiscoverItem[], filter: FavoriteFilter): DiscoverItem[] {
  return filter === 'all' ? items : items.filter((item) => favoriteKind(item) === filter);
}

/**
 * What the featured carousel draws from. Editor's picks only, until the
 * Customize sheet (#2292) offers the others: mixed with the caller's favorite
 * models, or those favorites alone.
 */
export type FeaturedSource = 'picks' | 'mixed' | 'favorites';

export const DEFAULT_FEATURED_SOURCE: FeaturedSource = 'picks';

/**
 * The carousel's models, at most `max`. Only favorite MODELS are featured
 * (the carousel opens in Build); "mixed" alternates a pick and a favorite,
 * newest favorite first, and lists a model once.
 */
export function featuredItems(
  picks: DiscoverItem[],
  favorites: DiscoverItem[],
  source: FeaturedSource,
  max: number,
): DiscoverItem[] {
  const favoriteModels = favorites.filter((item) => favoriteKind(item) === 'model');
  if (source === 'picks') return picks.slice(0, max);
  if (source === 'favorites') return favoriteModels.slice(0, max);

  const out: DiscoverItem[] = [];
  const seen = new Set<string>();
  for (let i = 0; out.length < max && (i < picks.length || i < favoriteModels.length); i++) {
    for (const item of [picks[i], favoriteModels[i]]) {
      if (item && out.length < max && !seen.has(item._id)) {
        seen.add(item._id);
        out.push(item);
      }
    }
  }
  return out;
}
